import React from "react";
import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";
// import * as markdownItEmoji from "markdown-it-emoji";
import * as markdownItAnchor from "markdown-it-anchor";
import * as markdownItFootnote from "markdown-it-footnote";
import * as markdownItTaskLists from "markdown-it-task-lists";
import * as markdownItHighlightjs from "markdown-it-highlightjs";
import "highlight.js/styles/github.css"; // Optional styling for code blocks

// Add table support
function preprocessConcepts(markdown) {
  if (!markdown) return '';
  
  markdown = markdown.replace('null', '-');
  // Regex to match concept lines
  const conceptLineRegex = /(\*\*(Vocabulary|Grammar) Concepts:\*\*)([\s\S]*?)(?=\n|$)/g;

  // Preprocess each matching line
  const updatedMarkdown = markdown.replace(conceptLineRegex, (match, header, type, body) => {
    // Split the body by commas to extract tokens
    const tokens = body.split(',')
      .map(token => token.trim())
      .filter(token => token.includes(':'));

    // Build styled tokens as HTML spans
    const processedTokens = tokens.map(token => {
      const [word, tag] = token.split(':');
      const tagClass = tag.trim().toLowerCase();
      return `<span class="concept-tag ${tagClass}">${word.trim()}</span>`;
    });

    // Return reconstructed line with header and processed tokens
    return `${header} ${processedTokens.join(', ')}`;
  });

  return updatedMarkdown;
}

// Function to convert conjugation to markdown table
function processConjugationTable(htmlContent) {
  // Find the conjugation section
  const conjugationRegex = /#### Conjugation \(if applicable\)([\s\S]*?)(?=####|$)/g;
  
  return htmlContent.replace(conjugationRegex, (match, conjugationContent) => {
    // Check if there's actual conjugation data
    if (conjugationContent.trim().length < 5 || !conjugationContent.includes('**')) {
      return match; // Return unchanged if no data
    }
    
    // Extract individual conjugation entries
    const conjugationEntries = conjugationContent.match(/\*\*([^*]+)\*\*:\s*([^\n]+)/g);
    
    if (!conjugationEntries) {
      return match; // Return unchanged if no entries found
    }
    
    // Start building the table
    let tableHtml = `#### Conjugation (if applicable)\n<div class="conjugation-table-container">
<table class="conjugation-table">
<tbody>`;
    
    // Add rows to the table
    for (const entry of conjugationEntries) {
      const [_, key, value] = entry.match(/\*\*([^*]+)\*\*:\s*([^\n]+)/) || [];
      if (key && value) {
        tableHtml += `
  <tr>
    <th>${key.trim()}</th>
    <td>${value.trim()}</td>
  </tr>`;
      }
    }
    
    // Close the table
    tableHtml += `
</tbody>
</table>
</div>`;
    
    return tableHtml;
  });
}

// Function to style answer synonyms section
function styleAnswerSynonyms(htmlContent) {
  return htmlContent.replace(
    /<div class="answer-synonyms"[^>]*>([\s\S]*?)<\/div>/g,
    '<div class="answer-synonyms" style="background-color: #f8f8f8; padding: 15px; border-radius: 8px; margin: 15px 0; color: #000; border-left: 3px solid #ddd;">$1</div>'
  );
}

// Function to add styling to paragraph elements
function addStyleToParagraphs(htmlContent) {
  // Use regex to add inline style to each <p> tag
  return htmlContent.replace(
    /<p>/g, 
    '<p style="background-color:#f2f2f2; padding:10px; border-radius: 10px;">'
  );
}

// Function to add styling to tables
function styleConjugationTables(htmlContent) {
  return htmlContent.replace(
    /<table class="conjugation-table">/g,
    '<table class="conjugation-table" style="width:100%; border-collapse:collapse; margin:15px 0; background-color:#fff; border-radius:8px; overflow:hidden; box-shadow:0 2px 3px rgba(0,0,0,0.1);">'
  ).replace(
    /<th>/g,
    '<th style="padding:10px; text-align:left; background-color:#f5f5f5; border-bottom:1px solid #ddd; font-weight:bold;">'
  ).replace(
    /<td>/g,
    '<td style="padding:10px; border-bottom:1px solid #eee;">'
  );
}

// Initialize Markdown-it with plugins and table support
const md = new MarkdownIt({ 
  html: true, 
  breaks: true,
  linkify: true,
  typographer: true
}) 
  // .use(markdownItEmoji.default)
  .use(markdownItAnchor.default)
  .use(markdownItFootnote.default)
  .use(markdownItTaskLists.default)
  .use(markdownItHighlightjs.default);

const MarkdownRenderer = ({ content }) => {
  if (!content) return null;
  
  let processedContent = preprocessConcepts(content);
  
  // Pre-process conjugation tables before rendering markdown
  processedContent = processConjugationTable(processedContent);
  
  let htmlContent = md.render(processedContent);
  
  // Apply styling
  htmlContent = styleAnswerSynonyms(htmlContent);
  htmlContent = addStyleToParagraphs(htmlContent);
  htmlContent = styleConjugationTables(htmlContent);
  
  // const sanitizedContent = DOMPurify.sanitize(htmlContent); // Security layer
  // return <div className="markdown-container" dangerouslySetInnerHTML={{ __html: sanitizedContent }} />;
  return <div className="markdown-container" style={{ lineHeight: 2, color: "black" }} dangerouslySetInnerHTML={{ __html: htmlContent }} />;
};

export default MarkdownRenderer;